import { 
  AdminStats, 
  AdminActivity, 
  AdminDashboardStatsResponse,
  AdminMetricsResponse,
  AdminHealthResponse,
  AdminETLStatusResponse,
  RecentQuery
} from '../types';
import { api } from '../../../../core/utils/apiClient';

export class AdminDashboardService {
  /**
   * Get complete admin dashboard statistics
   * Uses the optimized /api/v1/admin/stats endpoint
   */
  async getDashboardStats(): Promise<AdminStats> {
    try {
      const response = await api.get('/api/v1/admin/stats');
      if (response.ok) {
        const data: AdminDashboardStatsResponse = await response.json();
        return {
          totalCompanies: data.metrics.total_companies,
          totalDocuments: data.metrics.total_documents,
          totalQueries: data.metrics.total_queries,
          totalUsers: 0, // Not included in current API response
          activeCompanies: data.metrics.active_companies,
          processingDocuments: data.metrics.processing_documents,
          pendingETLTasks: data.metrics.pending_etl_tasks,
        };
      }
      throw new Error('Failed to fetch dashboard stats');
    } catch (error) {
      console.error('Error fetching admin dashboard stats:', error);
      return {
        totalCompanies: 0,
        totalDocuments: 0,
        totalQueries: 0,
        totalUsers: 0,
        activeCompanies: 0,
        processingDocuments: 0,
        pendingETLTasks: 0,
      };
    }
  }

  /**
   * Get complete dashboard data including system health, ETL status, and recent queries
   */
  async getCompleteDashboardData(): Promise<AdminDashboardStatsResponse | null> {
    try {
      const response = await api.get('/api/v1/admin/stats');
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching complete dashboard data:', error);
      return null;
        }
      }

  /**
   * Get lightweight metrics only
   */
  async getMetrics(): Promise<AdminMetricsResponse | null> {
    try {
      const response = await api.get('/api/v1/admin/metrics');
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching metrics:', error);
      return null;
    }
  }

  /**
   * Get system health status
   */
  async getHealthStatus(): Promise<AdminHealthResponse | null> {
    try {
      const response = await api.get('/api/v1/admin/health');
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching health status:', error);
      return null;
    }
  }

  /**
   * Get ETL status
   */
  async getETLStatus(): Promise<AdminETLStatusResponse | null> {
    try {
      const response = await api.get('/api/v1/admin/etl-status');
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching ETL status:', error);
      return null;
    }
  }

  /**
   * Get recent queries
   */
  async getRecentQueries(limit: number = 10): Promise<RecentQuery[]> {
    try {
      const response = await api.get(`/api/v1/admin/recent-queries?limit=${limit}`);
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.error('Error fetching recent queries:', error);
      return [];
    }
  }

  async getRecentActivity(): Promise<AdminActivity[]> {
    try {
      const queries = await this.getRecentQueries(10);
      
      return queries.map((q) => ({
        id: `query-${q.id}`,
        type: 'query' as const,
        title: 'AI Query',
        description: q.query_text.length > 60 ? q.query_text.substring(0, 60) + '...' : q.query_text,
        timestamp: q.created_at,
        metadata: { 
          status: q.status,
          company_id: q.company_id,
          company_name: q.company_name,
          response_time_ms: q.response_time_ms
        },
      }));
    } catch (error) {
      console.error('Error fetching admin activity:', error);
      return [];
    }
  }
}

export const adminDashboardService = new AdminDashboardService();

